import { addDays, dayDiff, parseISODate, startOfDay, toISODate } from "./dates";
import { DEFAULT_ITEMS, DEFAULT_OTHERS, freshState } from "./defaults";
import type { UnitDefaults } from "./defaults";
import { num } from "./normalize";
import type { Car, Entry, GarageState } from "./types";

/** Sample garage, word for word from the current app. Dates are shifted so the newest entry is a week old. */
const A = "c_s1", F = "c_s2", H = "c_s3";

const SAMPLE_CARS: readonly Car[] = [
  { id:"c_s1", plate:"ABC 907", nick:"Daily driver", make:"BMW", model:"E39 540i", year:1997, fuel:"Gasoline", owner:"Alex Morgan", contact:"+1 555 0101", reg:"2027-03-31", ins:"2027-01-15", notes:"Sample car. Delete before use." },
  { id:"c_s2", plate:"FRD 5678", nick:"Work pickup", make:"Ford", model:"Ranger", year:2021, fuel:"Diesel", owner:"Sam Rivera", contact:"+1 555 0102", reg:"2026-11-20", ins:"2027-05-01", notes:"Sample car. Delete before use." },
  { id:"c_s3", plate:"HND 2468", nick:"Family SUV", make:"Honda", model:"CR-V", year:2018, fuel:"Gasoline", owner:"Jamie Lee", contact:"+1 555 0103", reg:"2027-06-30", ins:"2026-12-05", notes:"Sample car. Delete before use." }
];

type SampleRow = readonly [string, string, number | null, string, string, number | null, number | null, string, string];

const SAMPLE_LOG: readonly SampleRow[] = [
  ["2024-03-10",A,150000,"Coolant Flush","Coolant flush and new thermostat",180,null,"Euro Auto Works","EA-0412"],
  ["2025-06-14",A,168000,"Spark Plugs","8 spark plugs, OEM",160,null,"Euro Auto Works","EA-0655"],
  ["2025-11-08",A,175100,"Engine Oil & Filter","Oil and filter, 5W-30 synthetic, 7.5 L",95,null,"QuickLube Center","JO-0981"],
  ["2025-11-08",A,175100,"Cabin / Aircon Filter","Cabin air filter",30,null,"QuickLube Center","JO-0981"],
  ["2025-12-12",F,23500,"Engine Oil & Filter","Oil and filter, 15W-40 diesel",95,null,"Main Street Garage","INV-1880"],
  ["2025-12-12",F,23500,"Fuel Filter","Diesel fuel filter",60,null,"Main Street Garage","INV-1880"],
  ["2026-01-15",A,176900,"Insurance","Comprehensive insurance renewal",780,null,"SafeDrive Insurance","POL-55120"],
  ["2026-01-20",H,54800,"Engine Oil & Filter","Oil and filter, 0W-20 synthetic",70,null,"QuickLube Center","JO-1012"],
  ["2026-01-20",H,54800,"Air Filter","Engine air filter",30,null,"QuickLube Center","JO-1012"],
  ["2026-01-20",H,54800,"Cabin / Aircon Filter","Cabin air filter",28,null,"QuickLube Center","JO-1012"],
  ["2026-02-10",H,55500,"Transmission Fluid","CVT fluid change",150,null,"Honda Service Center","HS-0156"],
  ["2026-02-14",A,177800,"Battery","New 80Ah AGM battery",210,null,"Battery Express","BE-0118"],
  ["2026-03-15",A,178400,"Registration / Road Tax","Annual registration renewal",150,null,"Motor Vehicle Office",""],
  ["2026-04-05",F,27000,"Emissions / Safety Inspection","Annual inspection, passed",45,null,"Inspection Center","IC-2207"],
  ["2026-04-11",H,57300,"Toll","Toll pass top-up",40,null,"Toll Pass",""],
  ["2026-04-20",A,179600,"Brake Fluid","Brake fluid flush, DOT 4",90,null,"Euro Auto Works","EA-0980"],
  ["2026-04-20",A,179600,"Air Filter","Engine air filter",35,null,"Euro Auto Works","EA-0980"],
  ["2026-05-01",F,27300,"Insurance","Comprehensive insurance renewal",640,null,"SafeDrive Insurance","POL-60311"],
  ["2026-05-09",A,180150,"Engine Oil & Filter","Oil and filter, 5W-30 synthetic, 7.5 L",98,null,"QuickLube Center","JO-1104"],
  ["2026-05-18",H,58200,"Wheel Alignment & Balancing","4-wheel alignment and balancing",90,null,"Tire Pro","TP-0790"],
  ["2026-06-10",A,180900,"Tire Rotation","Rotate and balance",30,null,"Tire Pro","TP-0812"],
  ["2026-06-15",F,28600,"Brake Pads (Front)","Front brake pads replaced",180,null,"Main Street Garage","INV-2231"],
  ["2026-06-20",F,28800,"Tire Rotation","Rotate and balance",30,null,"Tire Pro","TP-0822"],
  ["2026-06-30",H,59400,"Aircon Cleaning","Aircon cleaning and recharge",110,null,"Cool Air Auto","CA-0355"],
  ["2026-07-05",A,181600,"Wiper Blades","Front wiper blades",28,null,"Auto Parts Plus",""],
  ["2026-07-10",F,29000,"Fuel","Full tank",82,55,"Highway Fuel",""],
  ["2026-07-22",H,60000,"PMS / General Inspection","60,000 km inspection",120,null,"Honda Service Center","HS-0240"],
  ["2026-08-02",F,29590,"Fuel","Full tank",85,57,"Highway Fuel",""],
  ["2026-08-05",A,182500,"Fuel","Full tank",96,66,"City Fuel",""],
  ["2026-08-18",A,182900,"Electrical","Replaced driver window regulator",240,null,"Euro Auto Works","EA-1102"],
  ["2026-08-21",A,183020,"Fuel","Full tank",99,68,"City Fuel",""],
  ["2026-08-28",F,30210,"Fuel","Full tank",84,56,"Highway Fuel",""],
  ["2026-09-06",A,183540,"Fuel","Full tank",102,70,"City Fuel",""],
  ["2026-09-08",H,61200,"Engine Oil & Filter","Oil and filter, 0W-20 synthetic",72,null,"QuickLube Center","JO-1290"],
  ["2026-09-08",H,61200,"Brake Cleaning & Adjust","Clean and adjust all brakes",45,null,"QuickLube Center","JO-1290"],
  ["2026-09-12",F,30500,"Suspension","Replaced front stabilizer links",140,null,"Main Street Garage","INV-2310"],
  ["2026-09-20",H,61700,"Fuel","Full tank",58,45,"City Fuel",""],
  ["2026-09-24",A,184050,"Fuel","Full tank",97,67,"City Fuel",""],
  ["2026-09-25",F,30800,"Fuel","Full tank",87,58,"Highway Fuel",""],
  ["2026-09-30",A,184100,"Car Wash / Detailing","Full interior and exterior detail",120,null,"Shine Studio",""],
  ["2026-10-01",F,31000,"Toll","Toll pass top-up",50,null,"Toll Pass",""],
  ["2026-10-02",H,62000,"Tires (Replace)","4 new tires, 235/65R17",560,null,"Tire Pro","TP-0860"],
  ["2026-10-05",H,62240,"Fuel","Full tank",57,44,"City Fuel",""],
  ["2026-10-12",A,184350,"Fuel","Full tank",58,40,"City Fuel",""],
  ["2026-10-15",H,62400,"Parking","Monthly parking",60,null,"City Parking",""],
  ["2026-10-18",F,31380,"Fuel","Full tank",86,57.5,"Highway Fuel",""],
  ["2026-10-20",H,62800,"Fuel","Full tank",56,45,"City Fuel",""]
];

/** A new device's garage: the 3 sample cars and their entries (`sampleState`). */
export function sampleState(units: Partial<UnitDefaults> = {}, now: Date = new Date()): GarageState {
  const st = freshState(units, now);
  const anchor = parseISODate("2026-10-20")!;
  const shift = dayDiff(addDays(startOfDay(now), -7), anchor);
  const sh = (v: unknown) => { const d = parseISODate(v); return d ? toISODate(addDays(d, shift)) : ""; };
  st.cars = SAMPLE_CARS.map((c) => ({ ...c, reg: sh(c.reg), ins: sh(c.ins), sample: true }));
  const svc = new Set([...DEFAULT_ITEMS.map((i) => i.name), ...DEFAULT_OTHERS.filter((o) => o.group === "Repair").map((o) => o.name)]);
  const diy = new Set(["Wiper Blades", "Battery"]);
  st.log = SAMPLE_LOG.map((r, i): Entry => {
    const e: Entry = { id: `e_s${i}`, date: sh(r[0]), car: r[1], odo: r[2], item: r[3], desc: r[4], amount: r[5], qty: r[6], shop: r[7], notes: r[8], sample: true };
    const amount = num(e.amount);
    if (svc.has(e.item) && amount != null) {
      if (diy.has(e.item)) {
        e.diy = true; e.parts = amount; e.labor = null; e.desc = `${r[4]}, installed myself`;
      } else {
        const parts = Math.round(amount * 0.6);
        e.diy = false; e.parts = parts; e.labor = Math.round((amount - parts) * 100) / 100;
      }
    }
    return e;
  });
  return st;
}

/** Removes the sample cars and their entries; everything else stays (`clearSample`). */
export function withoutSample(st: GarageState): GarageState {
  const sampleIds = new Set(st.cars.filter((c) => c.sample).map((c) => c.id));
  const cars = st.cars.filter((c) => !c.sample);
  const keep = new Set(cars.map((c) => c.id));
  return { ...st, cars, log: st.log.filter((e) => !e.sample && keep.has(e.car) && !sampleIds.has(e.car)) };
}

/**
 * Adds a fresh set of samples, skipping any whose plate is already in use (`restoreSample`).
 * `uid` makes new ids; returns how many cars were added.
 */
export function withSample(st: GarageState, uid: (prefix: string) => string, now: Date = new Date()): { state: GarageState; added: number } {
  const fresh = sampleState({}, now);
  const clean = { cars: st.cars.filter((c) => !c.sample), log: st.log.filter((e) => !e.sample) };
  const plates = new Set(clean.cars.map((c) => c.plate.toLowerCase()));
  const ids = new Set(clean.cars.map((c) => c.id));
  const map = new Map<string, string>();
  const cars = [...clean.cars];
  for (const c of fresh.cars) {
    if (plates.has(c.plate.toLowerCase())) continue;
    const id = ids.has(c.id) ? uid("c") : c.id;
    map.set(c.id, id);
    cars.push({ ...c, id });
  }
  const log = [...clean.log];
  for (const e of fresh.log) { const car = map.get(e.car); if (car) log.push({ ...e, id: uid("e"), car }); }
  return { state: { ...st, cars, log }, added: map.size };
}
